import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HttpService } from '@Services/index';
import { OnboardingStatus, useBoundStore } from '@Store/index';
import { RoutesMap } from '@Router/router.types';
import { isLiliAppVersionAtLeast } from '@Services/utils/utils.ts';
import { useNavigationManager } from '@Router/useNavigationManager';
import { DeviceType } from '@Types/onboarding.types.ts';
import { trackEvent } from '@Services/analytics/tracking.service.ts';
import type { KybData } from '@Store/slices/kyb/kyb.types';
import { routesModel } from '@Router/router.consts.ts';

const MAX_POLLING_TIME = 60000;
const RETRY_DELAY = 2000;
const RETRY_TIMES = 6;

export const useUnifiedKybPolling = (
  onFinishedVerification?: () => void,
  pollingTime?: number,
  retryTime?: number,
  enabled: boolean = true
) => {
  const { navigateToSpecificPage } = useNavigationManager();
  const startTimeRef = useRef<number | null>(null);
  const pollingResolvedRef = useRef(false);
  const finishedVerificationTriggered = useRef(false);
  const [maxPollingTime, _setMaxPollingTime] = useState(pollingTime || MAX_POLLING_TIME);
  const [retryTimes, _setRetryTimes] = useState(retryTime || RETRY_TIMES);

  useEffect(() => {
    startTimeRef.current = Date.now();
  }, []);

  return useQuery({
    queryFn: getKybData,
    queryKey: ['getKybData'],
    retry: retryTimes,
    retryDelay: RETRY_DELAY,
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: (query) => {
      if (pollingResolvedRef.current) {
        return false;
      }

      const elapsedTime = startTimeRef.current ? Date.now() - startTimeRef.current : 0;

      // Timeout handling
      if (elapsedTime >= maxPollingTime) {
        const { routerData } = useBoundStore.getState().onboardingSlice;
        if (routerData.currentRoute !== routesModel[RoutesMap.UNDER_REVIEW].path) {
          navigateToSpecificPage(RoutesMap.UNDER_REVIEW);
        }
        return false;
      }

      const { data } = query.state;
      if (data) {
        const result = handleUnifiedKybStatus(
          data,
          navigateToSpecificPage,
          onFinishedVerification
            ? () => {
                if (onFinishedVerification && !finishedVerificationTriggered.current) {
                  finishedVerificationTriggered.current = true;
                  setTimeout(() => onFinishedVerification(), 0);
                }
              }
            : undefined
        );
        if (result === false) {
          pollingResolvedRef.current = true;
        }
        return result;
      }

      // Continue polling if no data yet
      return RETRY_DELAY;
    },
  });
};

// Mapping migration done using excel table created by Liran: https://docs.google.com/spreadsheets/d/1VAy3gpzig_fkCD-sYwlHO3Tqa57GZFsonX3en_BgvA4/edit?pli=1&gid=0#gid=0
export const getKybData = async (): Promise<KybData> => {
  const { customerId } = useBoundStore.getState().customer;
  const {
    setKybData,
    onboardingSlice: { getIsEmbedded },
  } = useBoundStore.getState();
  const url = getIsEmbedded()
    ? `v3/secureonboarding/partner/user/${customerId}/status`
    : `v3/secureonboarding/user/${customerId}/status`;

  const { data } = await HttpService.get<KybData>(url);
  setKybData(data);
  return data;
};

export const handleUnifiedKybStatus = (
  data: KybData,
  navigateToSpecificPage: (route: RoutesMap, params?: Record<string, unknown>) => void,
  handleFinishedVerificationCallback?: () => void
) => {
  const { deviceType } = useBoundStore.getState().onboardingSlice;
  const { prevOnboardingStatus, setPrevOnboardingStatus } = useBoundStore.getState();
  const onboardingStatus = data?.onboardingStatus;
  const documentsLength = data?.business?.missingLlcDocuments?.length || 0;

  const hasStatusChanged =
    prevOnboardingStatus !== null && prevOnboardingStatus !== onboardingStatus;
  const shouldTriggerIOSFinishEvent =
    deviceType === DeviceType.IOS && isLiliAppVersionAtLeast('26.2.1') && hasStatusChanged;
  setPrevOnboardingStatus(onboardingStatus ?? null);

  function triggerFinishEventIfNeeded() {
    if (shouldTriggerIOSFinishEvent) {
      window.location.replace('liliapp://socureFinished?status=' + onboardingStatus);
    }
  }

  // Priority 1: Success conditions
  if (onboardingStatus === OnboardingStatus.COMPLETED) {
    triggerFinishEventIfNeeded();
    navigateToSpecificPage(RoutesMap.SUCCESS);
    return false;
  }

  // Priority 2: Failure conditions
  const failureStatuses = [
    OnboardingStatus.KYC_FAILED,
    OnboardingStatus.KYB_FAILED_FINAL,
    OnboardingStatus.KYC_FAILED_FINAL,
    OnboardingStatus.KYC_FAILED_PENDING,
    OnboardingStatus.KYC_FAILED_PARTNER,
  ];

  if (onboardingStatus && failureStatuses.includes(onboardingStatus)) {
    triggerFinishEventIfNeeded();
    navigateToSpecificPage(RoutesMap.KYC_FAILED);
    return false;
  }

  // Priority 3: Document verification needed
  if (documentsLength > 0) {
    const moveToVerificationCenterStatuses = [
      OnboardingStatus.KYB_PENDING,
      OnboardingStatus.KYC_FAILED_UPLOADED_DOCS,
    ];

    if (onboardingStatus && moveToVerificationCenterStatuses.includes(onboardingStatus)) {
      triggerFinishEventIfNeeded();
      if (handleFinishedVerificationCallback) {
        // Non-US flow: Show modal first
        handleFinishedVerificationCallback();
      } else {
        // US flow: Direct navigation
        navigateToSpecificPage(RoutesMap.VERIFICATION_CENTER);
      }
      return false;
    }
  }

  // Priority 4: Special cases
  if (onboardingStatus === OnboardingStatus.KYC_FIRST_FAILED) {
    const { routerData } = useBoundStore.getState().onboardingSlice;
    trackEvent('user_entered_kyc_first_failed', {
      onboardingStatus,
      page: routerData.currentRoute,
    });
    if (routerData.currentRoute !== routesModel[RoutesMap.NON_US_SOCURE].path) {
      navigateToSpecificPage(RoutesMap.NON_US_SOCURE);
      return false;
    }
    return RETRY_DELAY;
  }

  // Priority 5: Continue polling for all other cases
  // Including KYB_PENDING + RESUBMIT, KYB_PENDING + PENDING_MIDDESK, etc.
  // Also handles missingLlcDocuments.length === 0
  return RETRY_DELAY;
};
